import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { APP } from '../config';

export const browserLanguage =
  (window.navigator as any)?.userLanguage || window.navigator.language;

export const mergeDateTime = (date: string, time: string) => {
  if (!date && !time) return null;
  if (!date || !moment(date).isValid()) return moment(time).format();
  if (!time || !moment(time).isValid()) return moment(date).format();

  return `${moment.parseZone(date).format().split('T')[0]}T${
    moment.parseZone(time).format().split('T')[1]
  }`;
};

export const getTimezoneFromDate = (date?: string | null) => {
  if (!date) return 0;
  return moment.parseZone(date).utcOffset() / 60;
};

export const useDateFormat = (value?: string) => {
  const { i18n } = useTranslation();

  const localeLanguage = useMemo(
    () => (i18n.language ? i18n.language : APP.locales.default),
    [i18n.language],
  );

  const momentDate = useMemo(
    () => (!!value ? moment.parseZone(value).locale(browserLanguage) : null),
    [value],
  );

  const fullDate = useMemo(
    () =>
      !!momentDate ? momentDate.locale(browserLanguage).format('L') : null,
    [momentDate],
  );

  const shortMonthDate = useMemo(
    () =>
      !!momentDate ? momentDate.locale(browserLanguage).format('ll') : null,
    [momentDate],
  );
  const dateTime = useMemo(
    () =>
      !!momentDate
        ? `${momentDate.locale(browserLanguage).format('L')} ${momentDate
            .locale(browserLanguage)
            .format('HH:mm')}`
        : null,
    [momentDate],
  );
  const date = useMemo(
    () =>
      !!momentDate ? momentDate.locale(browserLanguage).format('L') : null,
    [momentDate],
  );
  const time = useMemo(
    () =>
      !!momentDate ? momentDate.locale(browserLanguage).format('HH:mm') : null,
    [momentDate],
  );

  const quarter = useMemo(
    () => (!!momentDate ? momentDate.locale(browserLanguage).quarter() : null),
    [momentDate],
  );

  const year = useMemo(
    () => (!!momentDate ? momentDate.locale(browserLanguage).year() : null),
    [momentDate],
  );

  const shortMonth = useMemo(() => {
    if (!!momentDate) {
      const month = momentDate.locale(browserLanguage).format('MMMM');
      return month[0].toUpperCase() + month?.substring(1);
    } else {
      return null;
    }
  }, [momentDate]);

  const keyboardDatePickerFormat = useMemo(
    () => moment.localeData(browserLanguage).longDateFormat('L'),
    [],
  );

  const getDateWithTimezone = useCallback(
    (date?: string | null, tz?: number) => {
      if (!date) return null;
      const timezone = !!tz ? tz : 0;
      return moment(date).utcOffset(timezone).format();
    },
    [],
  );

  return {
    fullDate,
    shortMonthDate,
    dateTime,
    date,
    time,
    shortMonth,
    localeLanguage,
    quarter,
    year,
    getDateWithTimezone,
    keyboardDatePickerFormat,
  };
};

export const getTimePlaceholder = () => 'hh:mm';

export const useDatePlaceholder = () => {
  const { localeLanguage } = useDateFormat();

  const datePlaceholder = useMemo(() => {
    if (!!localeLanguage) {
      return moment.localeData(browserLanguage).longDateFormat('L');
    }
    return moment.localeData(browserLanguage).longDateFormat('L');
  }, [localeLanguage]);

  return { datePlaceholder };
};

export const objectLength = (obj: object) => {
  return !!obj ? Object.keys(obj)?.length : 0;
};
